import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileDown, Trash2, Plus, X, ArrowRightCircle, Printer, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VAT_RATE = 0.15;
const fmt = (n) => Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyClient = { company: '', contact_person: '', email: '', phone: '', address: '', vat_no: '' };

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const PartnerQuotes = ({ products, brandDiscounts, refreshOrders }) => {
  const [savedQuotes, setSavedQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);

  // Email-to-client modal state
  const [emailQuote, setEmailQuote] = useState(null); // the saved quote being emailed
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailCcPartner, setEmailCcPartner] = useState(true);
  const [emailSending, setEmailSending] = useState(false);

  // Builder state
  const [mode, setMode] = useState('client');
  const [lines, setLines] = useState([]); // {product, quantity, client_discount_pct}
  const [client, setClient] = useState(emptyClient);
  const [notes, setNotes] = useState('');
  const [save, setSave] = useState(false);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchQuotes(); }, []);

  const fetchQuotes = async () => {
    try {
      const { data } = await axios.get(`${API}/partner/quotes`, { withCredentials: true });
      setSavedQuotes(data);
    } catch {
      // silent — 401 during boot etc
    } finally {
      setLoading(false);
    }
  };

  const addLine = (product) => {
    if (lines.find((l) => l.product.sku === product.sku)) return;
    setLines([...lines, { product, quantity: 1, client_discount_pct: 0 }]);
  };
  const removeLine = (sku) => setLines(lines.filter((l) => l.product.sku !== sku));
  const updateLine = (sku, key, val) => setLines(lines.map((l) => l.product.sku === sku ? { ...l, [key]: val } : l));

  // Live totals
  const totals = lines.reduce((acc, l) => {
    const listExcl = Number(l.product.base_price_excl ?? l.product.base_price ?? 0);
    const listIncl = Number(l.product.base_price_incl ?? listExcl * (1 + VAT_RATE));
    const partnerDisc = Number(brandDiscounts?.[l.product.brand] || 0);
    const partnerCostExcl = listExcl * (1 - partnerDisc / 100);

    let unitExcl, unitIncl;
    if (mode === 'self') {
      unitExcl = partnerCostExcl;
      unitIncl = listIncl * (1 - partnerDisc / 100);
    } else {
      const cd = Number(l.client_discount_pct || 0);
      unitExcl = listExcl * (1 - cd / 100);
      unitIncl = listIncl * (1 - cd / 100);
    }
    acc.subtotalExcl += unitExcl * l.quantity;
    acc.subtotalIncl += unitIncl * l.quantity;
    acc.partnerCostExcl += partnerCostExcl * l.quantity;
    return acc;
  }, { subtotalExcl: 0, subtotalIncl: 0, partnerCostExcl: 0 });
  const marginExcl = mode === 'client' ? totals.subtotalExcl - totals.partnerCostExcl : 0;
  const marginPct = totals.partnerCostExcl > 0 ? (marginExcl / totals.partnerCostExcl) * 100 : 0;

  const buildPayload = () => ({
    mode,
    items: lines.map((l) => ({
      sku: l.product.sku,
      product_name: l.product.product_name || l.product.name || l.product.sku,
      quantity: Number(l.quantity),
      client_discount_pct: mode === 'client' ? Number(l.client_discount_pct || 0) : 0,
    })),
    client: mode === 'client' ? client : null,
    notes: notes || null,
    save,
  });

  const resetBuilder = () => {
    setLines([]); setClient(emptyClient); setNotes(''); setSave(false); setSearch(''); setMode('client');
  };

  const generatePdf = async () => {
    if (lines.length === 0) return toast.error('Add at least one product to the quote');
    setSubmitting(true);
    try {
      if (save) {
        // Save first, then download the saved quote's PDF
        const { data } = await axios.post(`${API}/partner/quotes`, buildPayload(), { withCredentials: true });
        toast.success('Quote saved');
        const resp = await axios.get(`${API}/partner/quotes/${data.id}/pdf`, { withCredentials: true, responseType: 'blob' });
        downloadBlob(new Blob([resp.data], { type: 'application/pdf' }), `EWSS-Quote-${data.id.slice(0, 8).toUpperCase()}.pdf`);
        fetchQuotes();
      } else {
        const resp = await axios.post(`${API}/partner/quotes/preview-pdf`, buildPayload(), { withCredentials: true, responseType: 'blob' });
        downloadBlob(new Blob([resp.data], { type: 'application/pdf' }), `Quote-preview.pdf`);
      }
      setBuilderOpen(false);
      resetBuilder();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate PDF');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadSavedPdf = async (q) => {
    try {
      const resp = await axios.get(`${API}/partner/quotes/${q.id}/pdf`, { withCredentials: true, responseType: 'blob' });
      downloadBlob(new Blob([resp.data], { type: 'application/pdf' }), `EWSS-Quote-${q.id.slice(0, 8).toUpperCase()}.pdf`);
    } catch (e) {
      toast.error('Failed to download PDF');
    }
  };
  const deleteQuote = async (q) => {
    if (!window.confirm('Delete this quote?')) return;
    try {
      await axios.delete(`${API}/partner/quotes/${q.id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchQuotes();
    } catch (e) { toast.error('Delete failed'); }
  };
  const convertToOrder = async (q) => {
    if (!window.confirm('Convert this quote into an order request to EWSS at your partner cost?')) return;
    try {
      const { data } = await axios.post(`${API}/partner/quotes/${q.id}/convert-to-order`, {}, { withCredentials: true });
      toast.success(`Quote converted → Order #${data.order_id.slice(0, 8).toUpperCase()}`);
      fetchQuotes();
      refreshOrders && refreshOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Convert failed'); }
  };

  const openEmailModal = (q) => {
    const clientName = q.client?.contact_person || q.client?.company || 'there';
    const businessName = q.partner_company || 'us';
    setEmailQuote(q);
    setEmailTo(q.client?.email || '');
    setEmailSubject(`Quote #${q.id.slice(0, 8).toUpperCase()} from ${businessName}`);
    setEmailBody(
      `Hi ${clientName},\n\n` +
      `Please find attached quote #${q.id.slice(0, 8).toUpperCase()}` +
      (q.client?.company ? ` for ${q.client.company}` : '') + `.\n\n` +
      `Total (incl. VAT): R ${fmt(q.subtotal_incl)}\n\n` +
      `Please let us know if you have any questions or would like to proceed.\n\n` +
      `Kind regards,\n${businessName}`
    );
    setEmailCcPartner(true);
  };

  const closeEmailModal = () => {
    setEmailQuote(null); setEmailTo(''); setEmailSubject(''); setEmailBody(''); setEmailCcPartner(true);
  };

  const sendEmail = async () => {
    if (!emailTo || !/.+@.+\..+/.test(emailTo)) return toast.error('Please enter a valid client email');
    if (!emailSubject.trim() || !emailBody.trim()) return toast.error('Subject and message are required');
    setEmailSending(true);
    try {
      await axios.post(
        `${API}/partner/quotes/${emailQuote.id}/email`,
        { to: emailTo.trim(), subject: emailSubject, body: emailBody, cc_partner: emailCcPartner },
        { withCredentials: true }
      );
      toast.success(`Quote emailed to ${emailTo}`);
      closeEmailModal();
      fetchQuotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  const filteredProducts = products.filter((p) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (p.sku || '').toLowerCase().includes(s) || (p.product_name || p.name || '').toLowerCase().includes(s);
  });

  if (loading) return <div className="text-gray-400">Loading quotes…</div>;

  return (
    <div className="space-y-6" data-testid="partner-quotes">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">My Quotes</h2>
          <p className="text-gray-400 text-sm">Build quotes for yourself (partner cost) or for your clients (list price less client discount).</p>
        </div>
        <button
          onClick={() => setBuilderOpen(true)}
          className="px-4 py-2 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:bg-ewss-cyan/90 flex items-center gap-2"
          data-testid="partner-quotes-new-btn"
        >
          <Plus size={16} /> New Quote
        </button>
      </div>

      {savedQuotes.length === 0 ? (
        <div className="text-center py-10 text-gray-400 bg-black/20 rounded-xl border border-gray-700">
          No saved quotes yet. Click <b>New Quote</b> to build one.
        </div>
      ) : (
        <div className="space-y-3">
          {savedQuotes.map((q) => (
            <div key={q.id} className="bg-black/40 border border-gray-700 rounded-xl p-4" data-testid={`saved-quote-${q.id.slice(0, 8)}`}>
              <div className="flex justify-between items-start flex-wrap gap-3">
                <div>
                  <div className="text-white font-bold">
                    Quote #{q.id.slice(0, 8).toUpperCase()} · <span className={q.mode === 'client' ? 'text-ewss-cyan' : 'text-yellow-400'}>{q.mode === 'client' ? 'Client' : 'Internal'}</span>
                    {q.converted_to_order_id && <span className="ml-2 text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">Converted</span>}
                  </div>
                  <div className="text-gray-400 text-xs">{new Date(q.created_at).toLocaleString('en-ZA')}</div>
                  {q.mode === 'client' && q.client?.company && <div className="text-gray-300 text-sm mt-1">For: <b>{q.client.company}</b></div>}
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-400">Total (incl VAT)</div>
                  <div className="text-ewss-cyan font-bold text-lg">R {fmt(q.subtotal_incl)}</div>
                  {q.mode === 'client' && q.margin_excl > 0 && (
                    <div className="text-xs text-green-400 mt-1">Margin: R {fmt(q.margin_excl)} ({q.margin_pct?.toFixed(1)}%)</div>
                  )}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap mt-3">
                <button onClick={() => downloadSavedPdf(q)} className="px-3 py-1.5 border border-ewss-cyan/30 text-ewss-cyan text-xs rounded hover:bg-ewss-cyan/10 flex items-center gap-1" data-testid={`saved-quote-pdf-${q.id.slice(0, 8)}`}>
                  <FileDown size={14} /> PDF
                </button>
                {q.mode === 'client' && (
                  <button onClick={() => openEmailModal(q)} className="px-3 py-1.5 border border-blue-400/40 text-blue-300 text-xs rounded hover:bg-blue-500/10 flex items-center gap-1" data-testid={`saved-quote-email-${q.id.slice(0, 8)}`}>
                    <Mail size={14} /> Email Client
                    {q.emailed_count > 0 && <span className="ml-1 text-[10px] text-gray-400">· sent {q.emailed_count}×</span>}
                  </button>
                )}
                {!q.converted_to_order_id && (
                  <button onClick={() => convertToOrder(q)} className="px-3 py-1.5 border border-green-500/30 text-green-400 text-xs rounded hover:bg-green-500/10 flex items-center gap-1" data-testid={`saved-quote-convert-${q.id.slice(0, 8)}`}>
                    <ArrowRightCircle size={14} /> Convert to Order
                  </button>
                )}
                <button onClick={() => deleteQuote(q)} className="px-3 py-1.5 border border-red-500/30 text-red-400 text-xs rounded hover:bg-red-500/10 flex items-center gap-1" data-testid={`saved-quote-delete-${q.id.slice(0, 8)}`}>
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {builderOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 p-4 overflow-y-auto" data-testid="quote-builder-modal">
          <div className="bg-ewss-navy border border-gray-700 rounded-xl max-w-5xl w-full my-6 p-6 space-y-5">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Build a Quote</h3>
              <button onClick={() => { setBuilderOpen(false); resetBuilder(); }} className="text-gray-400 hover:text-white" data-testid="quote-builder-close">
                <X size={20} />
              </button>
            </div>

            {/* Mode picker */}
            <div className="flex gap-3">
              {['client', 'self'].map((m) => (
                <button key={m} onClick={() => setMode(m)} data-testid={`quote-mode-${m}`}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border ${mode === m ? 'bg-ewss-cyan text-ewss-navy border-ewss-cyan' : 'border-gray-600 text-gray-300 hover:bg-white/5'}`}>
                  {m === 'client' ? 'For My Client (list less discount)' : 'For Myself (partner cost)'}
                </button>
              ))}
            </div>

            {mode === 'client' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-black/30 rounded-lg border border-gray-700">
                <input placeholder="Client company" value={client.company} onChange={(e) => setClient({ ...client, company: e.target.value })} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" data-testid="quote-client-company" />
                <input placeholder="Contact person" value={client.contact_person} onChange={(e) => setClient({ ...client, contact_person: e.target.value })} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
                <input placeholder="Email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
                <input placeholder="Phone" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />
                <input placeholder="Address" value={client.address} onChange={(e) => setClient({ ...client, address: e.target.value })} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm md:col-span-2" />
                <input placeholder="Client VAT number (optional)" value={client.vat_no} onChange={(e) => setClient({ ...client, vat_no: e.target.value })} className="px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm md:col-span-2" />
              </div>
            )}

            {/* Product picker */}
            <div>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products to add…" className="w-full px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm mb-2" data-testid="quote-product-search" />
              <div className="max-h-40 overflow-y-auto border border-gray-700 rounded">
                {filteredProducts.slice(0, 30).map((p) => (
                  <button key={p.sku} onClick={() => addLine(p)} className="w-full text-left px-3 py-1.5 border-b border-gray-800 hover:bg-white/5 text-sm flex justify-between" data-testid={`quote-add-product-${p.sku}`}>
                    <span className="text-white">{p.sku} · {p.product_name || p.name}</span>
                    <span className="text-gray-400">R {fmt(p.base_price_excl)}</span>
                  </button>
                ))}
                {filteredProducts.length === 0 && <div className="p-3 text-gray-400 text-sm">No products match your search.</div>}
              </div>
            </div>

            {/* Lines */}
            <div className="space-y-2">
              {lines.map((l) => {
                const listExcl = Number(l.product.base_price_excl ?? l.product.base_price ?? 0);
                const partnerDisc = Number(brandDiscounts?.[l.product.brand] || 0);
                const partnerCostExcl = listExcl * (1 - partnerDisc / 100);
                const unitExcl = mode === 'self' ? partnerCostExcl : listExcl * (1 - Number(l.client_discount_pct || 0) / 100);
                return (
                  <div key={l.product.sku} className="grid grid-cols-12 gap-2 items-center bg-black/30 p-2 rounded border border-gray-700 text-sm" data-testid={`quote-line-${l.product.sku}`}>
                    <div className="col-span-4 text-white">
                      <div className="font-medium">{l.product.product_name || l.product.name}</div>
                      <div className="text-xs text-gray-400">{l.product.sku} · list R {fmt(listExcl)}</div>
                    </div>
                    <div className="col-span-2">
                      <label className="text-[10px] text-gray-400">Qty</label>
                      <input type="number" min="1" value={l.quantity} onChange={(e) => updateLine(l.product.sku, 'quantity', Number(e.target.value) || 1)} className="w-full px-2 py-1 bg-ewss-navy border border-gray-600 rounded text-white" />
                    </div>
                    {mode === 'client' && (
                      <div className="col-span-2">
                        <label className="text-[10px] text-gray-400">Client disc %</label>
                        <input type="number" min="0" max="100" value={l.client_discount_pct} onChange={(e) => updateLine(l.product.sku, 'client_discount_pct', Number(e.target.value) || 0)} className="w-full px-2 py-1 bg-ewss-navy border border-gray-600 rounded text-white" data-testid={`quote-line-disc-${l.product.sku}`} />
                      </div>
                    )}
                    <div className={mode === 'client' ? 'col-span-3 text-right' : 'col-span-5 text-right'}>
                      <div className="text-xs text-gray-400">Unit excl / Line excl</div>
                      <div className="text-white">R {fmt(unitExcl)} · <span className="text-ewss-cyan font-semibold">R {fmt(unitExcl * l.quantity)}</span></div>
                      {mode === 'client' && <div className="text-[10px] text-gray-500">Cost R {fmt(partnerCostExcl * l.quantity)}</div>}
                    </div>
                    <div className="col-span-1 text-right">
                      <button onClick={() => removeLine(l.product.sku)} className="text-red-400 hover:bg-red-500/20 p-1 rounded"><Trash2 size={14} /></button>
                    </div>
                  </div>
                );
              })}
            </div>

            <textarea rows={2} placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full px-3 py-2 bg-ewss-navy border border-gray-600 rounded text-white text-sm" />

            {/* Summary */}
            <div className="p-4 bg-black/30 rounded-lg border border-ewss-cyan/30 space-y-1 text-sm">
              <div className="flex justify-between text-gray-300"><span>Subtotal (excl VAT)</span><span>R {fmt(totals.subtotalExcl)}</span></div>
              <div className="flex justify-between text-gray-400 text-xs"><span>VAT (15%)</span><span>R {fmt(totals.subtotalIncl - totals.subtotalExcl)}</span></div>
              <div className="flex justify-between text-white font-semibold pt-1 border-t border-gray-700"><span>Total (incl VAT)</span><span className="text-ewss-cyan">R {fmt(totals.subtotalIncl)}</span></div>
              {mode === 'client' && (
                <>
                  <div className="flex justify-between text-gray-400 text-xs pt-2"><span>Your partner cost (excl VAT)</span><span>R {fmt(totals.partnerCostExcl)}</span></div>
                  <div className="flex justify-between text-green-400 text-sm font-semibold"><span>Your margin</span><span>R {fmt(marginExcl)} ({marginPct.toFixed(1)}%)</span></div>
                </>
              )}
            </div>

            {/* Save toggle + submit */}
            <div className="flex justify-between items-center flex-wrap gap-3">
              <label className="flex items-center gap-2 text-white text-sm">
                <input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} data-testid="quote-save-toggle" />
                Save this quote to <b>My Quotes</b>
              </label>
              <button onClick={generatePdf} disabled={submitting || lines.length === 0} className="px-6 py-2.5 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:bg-ewss-cyan/90 disabled:opacity-50 flex items-center gap-2" data-testid="quote-generate-btn">
                <Printer size={16} /> {submitting ? 'Generating…' : (save ? 'Save & Download PDF' : 'Download PDF')}
              </button>
            </div>
          </div>
        </div>
      )}

      {emailQuote && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 p-4 overflow-y-auto" data-testid="quote-email-modal">
          <div className="bg-ewss-navy border border-gray-700 rounded-xl max-w-2xl w-full my-6 p-6 space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2"><Mail size={20} /> Email Quote to Client</h3>
                <p className="text-gray-400 text-xs mt-1">
                  Sent from <b>info@ewss.co.za</b> · Client replies go to your business email · PDF attached automatically
                </p>
              </div>
              <button onClick={closeEmailModal} className="text-gray-400 hover:text-white" data-testid="quote-email-close">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400 mb-1 block">To (client email)</label>
                <input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="client@example.com"
                  className="w-full px-3 py-2 bg-black/40 border border-gray-600 rounded text-white text-sm" data-testid="quote-email-to" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Subject</label>
                <input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full px-3 py-2 bg-black/40 border border-gray-600 rounded text-white text-sm" data-testid="quote-email-subject" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Message</label>
                <textarea rows={8} value={emailBody} onChange={(e) => setEmailBody(e.target.value)}
                  className="w-full px-3 py-2 bg-black/40 border border-gray-600 rounded text-white text-sm font-mono" data-testid="quote-email-body" />
              </div>
              <label className="flex items-center gap-2 text-sm text-white">
                <input type="checkbox" checked={emailCcPartner} onChange={(e) => setEmailCcPartner(e.target.checked)} data-testid="quote-email-cc-partner" />
                Send me a copy (CC my account email)
              </label>
              <div className="text-xs text-gray-500 bg-black/30 border border-gray-700 rounded p-2">
                <b>Attachment:</b> Quote-{emailQuote.id.slice(0, 8).toUpperCase()}.pdf (partner-branded)
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={closeEmailModal} className="px-4 py-2 border border-gray-600 text-gray-300 text-sm rounded hover:bg-white/5">Cancel</button>
              <button onClick={sendEmail} disabled={emailSending} className="px-6 py-2 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:bg-ewss-cyan/90 disabled:opacity-50 flex items-center gap-2" data-testid="quote-email-send">
                <Mail size={16} /> {emailSending ? 'Sending…' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerQuotes;
